import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { analytics } from './firebaseConfig';
import AppPageHeader from './components/AppPageHeader';
import { usePageMetadata } from './hooks/usePageMetadata';
import { getPublishingGuideRoute, PUBLISHING_GUIDES_ROUTE, publishingGuides, tolinoGuide, polishBookTagsGuide, PublishingGuide } from './publishingGuides';
import TolinoGuideBody from './TolinoGuideBody';
import PolishBookTagsGuideBody from './PolishBookTagsGuideBody';
import './Resources.css';
import './PublishingGuides.css';

const intro = 'Practical, country-specific resources for authors publishing translated editions. Learn how retailer ecosystems, distribution, metadata, pricing rules, and publishing conventions differ from market to market.';
const track = (event: string, params: Record<string, string> = {}) => {
  if (analytics) logEvent(analytics, event, params);
};

function GuideCards({ headingLevel = 2 }: { headingLevel?: 2 | 3 }) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return <div className="publishing-guides-grid">
    {publishingGuides.map(guide => <Link className="publishing-guide-card" to={getPublishingGuideRoute(guide)} key={guide.id}>
      <div className="publishing-guide-card-label">
        <span className="resources-kicker">Publishing handbook</span>
        <span className="publishing-guide-market">{guide.market}</span>
        <span className="publishing-guide-topics">{guide.topics.join(' · ')}</span>
      </div>
      <div className="publishing-guide-card-copy">
        <Heading>{guide.title}</Heading>
        <p>{guide.description}</p>
        <div className="publishing-guide-card-footer">
          <p className="publishing-guide-byline">{guide.author}, {guide.contributor}</p>
          <span className="publishing-guide-card-action">Read guide <span aria-hidden="true">→</span></span>
        </div>
      </div>
    </Link>)}
  </div>;
}

export function PublishingGuidesSection() {
  return <section className="resources-section-card resources-hub-section" aria-labelledby="publishing-guides-title">
    <div className="resources-section-heading"><div>
      <span className="resources-kicker">Learn about your next market</span>
      <h2 id="publishing-guides-title">Publishing Guides</h2>
      <p>Practical guides for authors publishing translated editions, including international retailers, distribution, metadata, localization, and country-specific publishing requirements.</p>
    </div></div>
    <GuideCards headingLevel={3} />
  </section>;
}

function Breadcrumbs({ article }: { article?: string }) {
  return <nav aria-label="Breadcrumb" className="publishing-guides-breadcrumb">
    <ol>
      <li><Link to="/resources">Resources</Link></li>
      <li>{article ? <Link to={PUBLISHING_GUIDES_ROUTE}>Publishing Guides</Link> : <span aria-current="page">Publishing Guides</span>}</li>
      {article && <li><span aria-current="page">{article}</span></li>}
    </ol>
  </nav>;
}

export function PublishingGuidesIndex() {
  usePageMetadata('Publishing Guides | ScribeShadow', intro);
  useEffect(() => { track('publishing_guides_viewed'); }, []);
  return <main className="resources-page-shell publishing-guides-page">
    <div className="resources-page-inner">
      <Breadcrumbs />
      <AppPageHeader variant="banner" title="Publishing Guides" subtitle={intro} titleId="publishing-guides-page-title" />
      <GuideCards />
    </div>
  </main>;
}

function ContributorLink({ children, placement, guide = tolinoGuide }: { children: React.ReactNode; placement: string; guide?: PublishingGuide }) {
  return <a href={guide.contributorUrl} target="_blank" rel="noopener noreferrer"
    onClick={() => track('publishing_guide_contributor_clicked', { guideId: guide.id, placement })}>{children}</a>;
}

export function PolishBookTagsGuidePage() {
  const guide = polishBookTagsGuide;
  usePageMetadata(`${guide.title} | ScribeShadow`, guide.metaDescription);
  useEffect(() => { track('publishing_guide_opened', { guideId: polishBookTagsGuide.id }); }, []);
  return <main className="resources-page-shell publishing-guides-page">
    <div className="resources-page-inner publishing-guide-inner">
      <Breadcrumbs article="Polish Book Tags" />
      <article className="publishing-guide-article" aria-labelledby="polish-book-tags-title">
        <header className="publishing-guide-header">
          <span className="resources-kicker">Publishing Guides · Poland</span>
          <AppPageHeader title={guide.title} subtitle={guide.subtitle} titleId="polish-book-tags-title" />
          <p className="publishing-guide-byline">By <ContributorLink guide={guide} placement="byline">{guide.author}, {guide.contributor}</ContributorLink></p>
        </header>
        <section className="resources-section-card publishing-guide-introduction" aria-labelledby="polish-guide-introduction-title">
          <span className="resources-kicker">From ScribeShadow</span>
          <h2 id="polish-guide-introduction-title">Find the words your Polish readers use</h2>
          <p>Choosing keywords for a translated book takes more than translating the English terms. Readers in each market develop their own mix of genre labels, trope names and borrowed English expressions.</p>
          <p>In this resource, Anastasia Rydaeva explores the tags used on the Polish book community Lubimyczytać, focusing on dark romance and romantasy. Her tables compare Polish and English terms, the number of books carrying each tag, and shelf counts for leading titles—giving authors a practical starting point for researching how to describe their books for Polish readers.</p>
        </section>
        <PolishBookTagsGuideBody />
        <section className="resources-section-card publishing-guide-contributor" aria-labelledby="polish-guide-contributor-title">
          <h2 id="polish-guide-contributor-title">About the contributor</h2>
          <p>Anastasia Rydaeva is a ScribeShadow Group Expert and the founder of Cowper Author Services, which provides language-specific editing, localization, and international publishing services for authors.</p>
          <ContributorLink guide={guide} placement="contributor">Visit Cowper Author Services</ContributorLink>
          <p className="publishing-guide-clarification">Cowper Author Services is an independent company. Educational contributions to ScribeShadow do not make Cowper or its team employees of ScribeShadow.</p>
        </section>
      </article>
      <Link to={PUBLISHING_GUIDES_ROUTE}>← Back to Publishing Guides</Link>
    </div>
  </main>;
}

export function TolinoGuidePage() {
  usePageMetadata(`${tolinoGuide.title} | ScribeShadow`, tolinoGuide.metaDescription);
  useEffect(() => { track('publishing_guide_opened', { guideId: tolinoGuide.id }); }, []);
  return <main className="resources-page-shell publishing-guides-page">
    <div className="resources-page-inner publishing-guide-inner">
      <Breadcrumbs article="Tolino for Indie Authors" />
      <article className="publishing-guide-article" aria-labelledby="tolino-guide-title">
        <header className="publishing-guide-header">
          <span className="resources-kicker">Publishing Guides · Germany</span>
          <AppPageHeader title={tolinoGuide.title} subtitle={tolinoGuide.subtitle} titleId="tolino-guide-title" />
          <p className="publishing-guide-byline">By <ContributorLink placement="byline">{tolinoGuide.author}, {tolinoGuide.contributor}</ContributorLink></p>
        </header>
        <section className="resources-section-card publishing-guide-introduction" aria-labelledby="guide-introduction-title">
          <span className="resources-kicker">From ScribeShadow</span>
          <h2 id="guide-introduction-title">Translating your book is only the first step</h2>
          <p>At ScribeShadow, our goal is to make high quality book translation accessible to independent authors. But producing a translated edition raises another question almost immediately: what do you do with it once it is finished?</p>
          <p>Publishing internationally is not always as simple as taking the strategy that works in the United States and repeating it in another language. Retailers, distribution systems, pricing rules, metadata conventions, and reader expectations can vary significantly from one market to another.</p>
          <p>To help authors navigate those differences, we're working with international publishing experts to build practical resources for authors publishing translated editions.</p>
          <p>This guide comes from Anastasia Rydaeva of Cowper Author Services, a ScribeShadow Group Expert whose team works with authors publishing and localizing books across multiple European markets.</p>
          <p>We are starting with Germany and Tolino, an ecosystem many English language indie authors encounter primarily as another distribution option. As Anastasia explains below, there are important differences between simply distributing a German edition to Tolino and understanding how German indie authors use the platform directly.</p>
        </section>
        <TolinoGuideBody />
        <section className="resources-section-card publishing-guide-contributor" aria-labelledby="guide-contributor-title">
          <h2 id="guide-contributor-title">About the contributor</h2>
          <p>Anastasia Rydaeva is a ScribeShadow Group Expert and the founder of Cowper Author Services, which provides language-specific editing, localization, and international publishing services for authors.</p>
          <ContributorLink placement="contributor">Visit Cowper Author Services</ContributorLink>
          <p className="publishing-guide-clarification">Cowper Author Services is an independent company. Educational contributions to ScribeShadow do not make Cowper or its team employees of ScribeShadow.</p>
        </section>
      </article>
      <Link to={PUBLISHING_GUIDES_ROUTE}>← Back to Publishing Guides</Link>
    </div>
  </main>;
}
